import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Alert, Container, Slide, Snackbar } from '@mui/material';
import Login from './components/Login';
import Dashboard from './components/Dashboards/Dashboard';
import ResponsiveAppBar from './components/CommonComponents/ResponsiveAppBar';
import VisitorParking from './components/CreateFeatures/VisitorParking';
import AmenityBookingPage from './components/CreateFeatures/AminityBookingPage';
import ServiceRequestForm from './components/CreateFeatures/ServiceRequestForm';
import UnitDashboard from './components/Dashboards/UnitDashboard';
import BookingCheckout from './components/BookingCheckout';
import ServiceRequestView from './components/ViewById/ServiceRequestView';
import AmenityBookingCalendar from './components/CreateFeatures/AmenityBookingCalendar';
import AnnouncementView from './components/ViewById/AnnouncementView';
import CreateAnnouncement from './components/CreateFeatures/CreateAnnouncement';
import ViewAnnouncementTable from './components/ViewTables/ViewAnnouncementTable';
import ViewServiceRequestsTable from './components/ViewTables/ViewServiceRequestsTable';
import ViewBookingsTable from './components/ViewTables/ViewBookingsTable';
import ViewVisitorParkingTable from './components/ViewTables/ViewVisitorParkingTable';
import ViewViolationsTable from './components/ViewTables/ViewViolationsTable';
import CreateViolation from './components/CreateFeatures/CreateViolation';
import MenuOptions from './components/CommonComponents/MenuOptions';
import NotFound from './components/CommonComponents/NotFound';
import SendAnnouncement from './components/CreateFeatures/SendAnnoucement';
import ViewUnitFile from './components/ViewTables/ViewUnitFile';
import BookingConfirmation from './components/BookingConfirmation';
import BookingView from './components/ViewById/BookingView';
import ViolationView from './components/ViewById/ViolationView';
import { AuthContextProvider, useAuth } from './context/authContext';
import ForgotPassword from './components/ForgotPassword';
import { UserType } from './Types/UnitFeatureTypes';
import ViewToDoTable from './components/ViewTables/ViewToDoTable';
import Library from './components/Library';
import UnderConstruction from './components/CommonComponents/UnderConstruction';
import LoadingOverlay from './components/CommonComponents/LoadingOverlay';
import AccountPage from './components/CommonComponents/AccountPage';
import AccountSettings from './components/CommonComponents/AccountSetting';
import { useApp } from './context/appContext';
import Events from './components/Events';
import CreateEvent from './components/CreateFeatures/CreateEvent';
import WarningDialog from './components/CommonComponents/WarningDialog';

const App = () => {
  const [menuWidth, setMenuWidth] = React.useState<number>(250);

  const { isAuthenticated, user } = useAuth();
  const { snackbar, loading, setSnackbar, warningDialog } = useApp();
  const managerSx = {
    mt: 15,
    ml: menuWidth === 250 ? 35 : 20,
    animation: 'margin-left 0.5s',
    transition: 'margin-left 0.2s',
    '@media (max-width: 600px)': {
      ml: menuWidth === 250 ? 35 : 10,
    },
  };
  
  const getDashboardComponent = () => {
    switch (user?.type) {
      case UserType.MANAGER:
        return <Dashboard />;
      case UserType.OWNER:
        return <UnitDashboard />;
      // case UserType.SECURITY:
      //   return <SecurityDashboard />;
      // case UserType.BOARD_MEMBER:
      //   return <BoardMemberDashboard />;
      // case UserType.TENANT:
      //   return <TenantDashboard />;
      default:
        return <Dashboard />;
    }
  };

  const ProtectedRoute = ({ element, isAuthenticated }: { element: React.ReactElement, isAuthenticated: boolean }) => {
    return isAuthenticated ? element : <Navigate to="/login" />;
  };

  const UnprotectedRoute = ({ element, isAuthenticated }: { element: React.ReactElement, isAuthenticated: boolean }) => {
    return !isAuthenticated ? element : <Navigate to="/" />;
  };

  const onSnackbarClose = (event: React.SyntheticEvent | Event, reason: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(false, '', 'success');
  };



  return (
    <Router>
      <ResponsiveAppBar />
      {isAuthenticated && <MenuOptions menuWidth={menuWidth} setMenuWidth={setMenuWidth} />}
      <Container maxWidth={isAuthenticated ? 'lg' : 'xl'} sx={isAuthenticated ? managerSx : {mt: 15}}>
        <Routes>
          <Route path="*" element={<NotFound />} />
          <Route path="/" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={getDashboardComponent()} />} />
          <Route path="/dashboard" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={getDashboardComponent()} />} />

          <Route path="/login" element={<UnprotectedRoute isAuthenticated={isAuthenticated} element={<Login />} />} />
          <Route path="/forgotpassword" element={<UnprotectedRoute isAuthenticated={isAuthenticated} element={<ForgotPassword />} />} />
          <Route path="/requestVisitorParking" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<VisitorParking />} />} />
          <Route path="/externalVisitorParking" element={<UnprotectedRoute isAuthenticated={isAuthenticated} element={<VisitorParking />} />} />

          <Route path="/createBooking" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<AmenityBookingPage />} /> } />
          <Route path="/createBookingCalender" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<AmenityBookingCalendar />} />} />
          <Route path="/createServiceRequest" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<ServiceRequestForm onSubmit={() => {}} />} />} />
          <Route path="/createAnnouncement" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<CreateAnnouncement />} />} />
          <Route path="/sendAnnouncement" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<SendAnnouncement />} />} />
          <Route path="/createViolation" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<CreateViolation />} />} />
          <Route path="/createEvent" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<CreateEvent />} />} />

          <Route path="/editAnnouncement" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<CreateAnnouncement />} />} />

          <Route path="/viewAnnouncement" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<AnnouncementView />} />} />
          <Route path="/viewServiceRequest" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<ServiceRequestView />} />} />
          <Route path="/viewBooking" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<BookingView />} />} />
          <Route path="/viewViolation" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<ViolationView />} />} />
          <Route path="/viewVisitorParking" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<UnderConstruction />} /> } />

          <Route path="/announcements" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<ViewAnnouncementTable  />} />} />
          <Route path="/serviceRequests" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<ViewServiceRequestsTable  />} />} />
          <Route path="/bookings" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<ViewBookingsTable  />} />} />
          <Route path="/visitorParkings" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<ViewVisitorParkingTable />} />} />
          <Route path="/violations" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<ViewViolationsTable />} />} />
          <Route path="/unitFile" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<ViewUnitFile />} />} />
          <Route path="/todos" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<ViewToDoTable />} />} />
          <Route path="/library" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<Library />} />} />
          
          <Route path="/bookingCheckout" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<BookingCheckout />} />} />
          <Route path="/confirmBooking" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<BookingConfirmation />} />} />
          {/* <Route path="/account" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<AccountSettings />} />} /> */}
          <Route path="/account" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<AccountPage />} />} />
          <Route path="/events" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<Events />} />} />
          {/* <Route path="/unitDashboard" element={<UnitDashboard />} /> */}
          {/* <Route path="/managementDashboard" element={<Dashboard />} /> */}

          //TODO: Add routes for different user types
          {/* <Route path="/managementDashboard" element={<Dashboard />} />
          <Route path="/boardMemberDashboard" element={<Dashboard />} />
          <Route path="/residentDashboard" element={<Dashboard />} />
          <Route path="/securityDashboard" element={<Dashboard />} /> */}
        </Routes>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={onSnackbarClose}
          message={snackbar.message}
          typeof={snackbar.severity}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}  // You can adjust position if necessary
          sx={{
            position: 'fixed',
            zIndex: (theme) => theme.zIndex.snackbar + 1,  // Ensure Snackbar is above other elements
          }}
          slots={{transition: (props) => <Slide {...props} direction="up" />}}
        >
          <Alert severity={snackbar.severity} variant='standard' sx={{ width: '100%' }}>{snackbar.message}</Alert>
        </Snackbar>
        <LoadingOverlay isLoading={loading} />
        <WarningDialog
          open={warningDialog.open}
          title={warningDialog.title}
          message={warningDialog.message}
          onClose={warningDialog.onCancel}
          onConfirm={warningDialog.onConfirm}
        />
      </Container>
    </Router>
  );
};

export default App;
